import moment from 'moment'
import queryString from 'query-string'
import React from 'react'
import store from 'store'

import agent from 'agent'
import {
  CompanyEntitlementResponse,
  LimitTrackingPeriod
} from 'types/api/companies/entitlements'
import { CompanyPreferences } from 'types/api/rbac/company'
import {
  RbacBody,
  RbacPrivilege,
  RbacRolesAll,
  RbacRoleTeam
} from 'types/api/rbac/rbac'
import {
  UserSession,
  UserSessionTeam
} from 'types/api/users/sessions/user-session'
import {
  flattenEntitlements,
  getEntitlementsExpirationProperties
} from 'utils/company-entitlements'
import { formatTimePeriodDate } from 'utils/formatDate'
import { ms } from 'utils/millisecond'
import { isPlainObject } from 'utils/obj'
import { RBAC as RBACFallback } from 'utils/rbac-typed'

type Limits = 'clients' | 'services' | 'targets' | 'teams' | 'users'

interface AuthContextEntitlementValues {
  contractExpirationImminent: boolean
  daysUntilContractExpiration: number
  hasAgentBilling: boolean
  hasReliabilityManagement: boolean
  hasFaultInjection: boolean
  hasFailureFlags: boolean
  isInternal: boolean
  isStrict: boolean
  isTrial: boolean
  limits: {
    [key in Limits]: number
  }
}

export interface AuthContextValues extends AuthContextEntitlementValues {
  RBAC: RbacBody
  authHeader: string
  companyDisplayName: string
  companyId: string
  companyIsAlfiEnabled: boolean // todo: EN-5114 deprecate
  companyPrefs: CompanyPreferences
  companyPrivileges: RbacPrivilege[]
  currentTeam: UserSessionTeam
  currentTeamId: string
  currentTeamRoles: RbacRoleTeam[]
  endSession: () => Promise<void>
  fetchSession: () => Promise<any>
  getTeamDetails: (
    teamId: string
  ) => UserSessionTeam | typeof TEAM_DETAILS_FALLBACK
  hasCompanyPrivilege: (
    privilege: RbacPrivilege,
    shouldAutoMigrate?: boolean
  ) => boolean
  hasElevatedCompanyRole: boolean
  hasTeamPrivilege: (
    teamId: string,
    privilege: RbacPrivilege,
    shouldAutoMigrate?: boolean
  ) => boolean
  isProductionEnvironment: boolean
  isRbacEnabled: boolean
  privilegesForTeam: (teamId: string) => RbacPrivilege[]
  ready: boolean
  renewSession: () => Promise<any>
  roles: RbacRolesAll[]
  session: UserSession
  startSession(activeSession?: UserSession, activeTeamId?: string): Promise<any>
  teams: UserSessionTeam[]
  setActiveTeamId: (teamId: string) => Promise<void>
  setAllAuth: React.Dispatch<
    React.SetStateAction<{
      ready: boolean
      session: UserSession
      currentTeamId: string
      isRbacEnabled: boolean
      RBAC: RbacBody
    }>
  >
}

export const TEAM_DETAILS_FALLBACK = {
  header: '',
  privileges: [] as RbacPrivilege[],
  roles: [] as RbacRolesAll[]
}

export const BASE_COMPANY_ENTITLEMENTS_RESPONSE: CompanyEntitlementResponse = {
  company_id: '',
  created_at: formatTimePeriodDate(moment().subtract(32, 'days')),
  end: formatTimePeriodDate(moment().add(8, 'days')),
  internal: false,
  strict: true,
  has_agent_billing: true,
  has_trial_banners: false,
  product_flags: {
    hasProductFI: false,
    hasProductRM: false,
    hasProductFF: false
  },
  limits: {
    limitTargets: 0,
    limitUsers: 0,
    limitServices: 0,
    limitAgents: 0,
    limitTeams: 0
  },
  targets_tracking_period: LimitTrackingPeriod.Monthly
}

export const BASE_SESSION: UserSession = {
  company_id: '',
  company_name: '',
  company_alias: '',
  company_is_alfi_enabled: false,
  company_entitlements_response: BASE_COMPANY_ENTITLEMENTS_RESPONSE,
  roles: [],
  privileges: [],
  expires_at: '',
  header: '',
  identifier: '',
  first_name: '',
  last_name: '',
  teams: [],
  last_login: '',
  renew_token: '',
  token: '',
  prefs: {} as CompanyPreferences
}

export const DEFAULT_AUTH_VALUES: AuthContextValues = {
  RBAC: RBACFallback /** The RBAC roles and privileges summary */,
  authHeader:
    BASE_SESSION.header /** The authorization header used for requests */,
  companyDisplayName:
    BASE_SESSION.company_name /** Preferred display name of the company */,
  companyId: BASE_SESSION.company_id,
  companyIsAlfiEnabled: BASE_SESSION.company_is_alfi_enabled,
  companyPrefs: BASE_SESSION.prefs,
  companyPrivileges: [],
  contractExpirationImminent: false,
  currentTeam: {} as UserSessionTeam,
  currentTeamId: '',
  currentTeamRoles: [],
  daysUntilContractExpiration: 8,
  endSession: async () => {},
  fetchSession: async () => {},
  getTeamDetails: () => TEAM_DETAILS_FALLBACK,
  hasAgentBilling: BASE_COMPANY_ENTITLEMENTS_RESPONSE.has_agent_billing,
  hasCompanyPrivilege: () => false,
  hasElevatedCompanyRole: false,
  hasReliabilityManagement:
    BASE_COMPANY_ENTITLEMENTS_RESPONSE.product_flags.hasProductRM,
  hasFaultInjection:
    BASE_COMPANY_ENTITLEMENTS_RESPONSE.product_flags.hasProductFI,
  hasFailureFlags:
    BASE_COMPANY_ENTITLEMENTS_RESPONSE.product_flags.hasProductFF,
  hasTeamPrivilege: () => false,
  isProductionEnvironment: false,
  isRbacEnabled: false,
  isInternal: BASE_COMPANY_ENTITLEMENTS_RESPONSE.internal,
  isStrict: BASE_COMPANY_ENTITLEMENTS_RESPONSE.strict,
  isTrial: BASE_COMPANY_ENTITLEMENTS_RESPONSE.has_trial_banners,
  limits: {
    clients: BASE_COMPANY_ENTITLEMENTS_RESPONSE.limits.limitAgents,
    services: BASE_COMPANY_ENTITLEMENTS_RESPONSE.limits.limitServices,
    targets: BASE_COMPANY_ENTITLEMENTS_RESPONSE.limits.limitTargets,
    teams: BASE_COMPANY_ENTITLEMENTS_RESPONSE.limits.limitTeams,
    users: BASE_COMPANY_ENTITLEMENTS_RESPONSE.limits.limitUsers
  },
  privilegesForTeam: () => [],
  ready: false,
  renewSession: async () => {},
  roles: BASE_SESSION.roles,
  session: BASE_SESSION,
  startSession: async () => {},
  setActiveTeamId: async () => {},
  setAllAuth: () => {},
  teams: []
}

export async function getRbacSummary(
  session: UserSession
  // todo: return type should be RbacBody
): Promise<RbacBody | undefined> {
  let rbacSummary
  try {
    // do not cache the data from this call
    // the endpoint is fast and returns static data
    // and if it changes, we need it to be updated in the browser asap
    const { data } = await agent.get<RbacBody>('/roles', {
      headers: {
        Authorization: session.header
      }
    })

    rbacSummary = data

    // cache recently-retrieved data in local storage
    store.set('rbac-summary', {
      date: new Date(),
      summary: rbacSummary
    })
  } catch (e) {
    // use previously cached copy if the api call fails
    const storedSummary = store.get('rbac-summary')
    if (storedSummary) {
      const { date, summary } = storedSummary
      // Only refresh summary if older than 24 hours
      if (
        date &&
        summary &&
        // @ts-expect-error Date math works fine
        Number(new Date() - new Date(date)) <= ms.days(1)
      ) {
        return summary
      }
    } else {
      // and if we don't have it cached then use our fixture copy of the RBAC data
      rbacSummary = RBACFallback
    }
  }

  return rbacSummary
}

export async function getUserSession(session: UserSession) {
  const res = await agent.get<UserSession>(
    '/users/sessions?getCompanySession=true',
    {
      headers: {
        Authorization: session.header
      }
    }
  )
  return res.data
}

export async function renewUserSession(session: UserSession) {
  // todo: determine type of response
  const res = await agent.post(
    '/users/renew/rbac',
    queryString.stringify({
      email: session.identifier,
      companyId: session.company_id,
      renewToken: session.renew_token
    }),
    {
      headers: {
        'Content-Type': 'application/x-www-form-urlencoded'
      }
    }
  )
  return res.data
}

export function setActiveTeamIdInLocalStorage(
  teamId: string,
  companyId: string
) {
  const activeTeamIdValue = store.get('lastActiveOrgId')
  if (isPlainObject(activeTeamIdValue)) {
    return store.set('lastActiveOrgId', {
      ...activeTeamIdValue,
      [companyId]: teamId
    })
  } else {
    store.set('lastActiveOrgId', {
      [companyId]: teamId
    })
  }
}

export function getActiveTeamIdFromLocalStorage(companyId: string) {
  const activeTeamIdValue: string | { [k: string]: string } =
    store.get('lastActiveOrgId')

  if (typeof activeTeamIdValue === 'string') {
    return activeTeamIdValue
  } else if (isPlainObject(activeTeamIdValue)) {
    return activeTeamIdValue[companyId]
  }
}

export function getEntitlementsForAuth(
  session: UserSession
): AuthContextEntitlementValues {
  const entitlements = flattenEntitlements(
    // todo: EN-6511 remove company_entitlements_response after migration
    // this makes it backwards-compatible so deploy doesn't have to be coordinated
    session?.company_entitlements_response ??
      session?.entitlements ??
      BASE_COMPANY_ENTITLEMENTS_RESPONSE
  )

  const { contractExpirationImminent, daysUntilContractExpiration } =
    getEntitlementsExpirationProperties(entitlements.dateEnd)

  return {
    contractExpirationImminent,
    daysUntilContractExpiration,
    hasAgentBilling: entitlements.hasAgentBilling,
    hasFaultInjection: entitlements.hasProductFI,
    hasReliabilityManagement: entitlements.hasProductRM,
    hasFailureFlags: entitlements.hasProductFF,
    isInternal: entitlements.isInternal,
    isStrict: entitlements.isStrict,
    isTrial: entitlements.hasTrialBanners,
    limits: {
      clients: entitlements.limitAgents,
      services: entitlements.limitServices,
      targets: entitlements.limitTargets,
      teams: entitlements.limitTeams,
      users: entitlements.limitUsers
    }
  }
}

/**
 * EN-6460 mapping for old to new privilege migration: [old, new]
 */
const PRIVILEGE_MAP: [RbacPrivilege, RbacPrivilege][] = [
  [RbacPrivilege.ACCESS_SECURITY_LOGS, RbacPrivilege.SECURITY_REPORTS_READ],
  [RbacPrivilege.ACTIVATE_CLIENT, RbacPrivilege.CLIENTS_WRITE],
  [RbacPrivilege.ACTIVATE_USER, RbacPrivilege.COMPANY_USERS_WRITE],
  [RbacPrivilege.ADD_REMOVE_USER_FROM_TEAM, RbacPrivilege.USERS_WRITE],
  [RbacPrivilege.ADD_USER_TO_COMPANY, RbacPrivilege.COMPANY_USERS_WRITE],
  [RbacPrivilege.ADD_USER_TO_COMPANY, RbacPrivilege.USERS_WRITE],
  [RbacPrivilege.CREATE_REMOVE_TEAM, RbacPrivilege.TEAMS_WRITE],
  [RbacPrivilege.CREATE_SCENARIO, RbacPrivilege.SCENARIOS_WRITE],
  [RbacPrivilege.EDIT_SCENARIO, RbacPrivilege.SCENARIOS_WRITE],
  [
    RbacPrivilege.MANAGE_COMPANY_USER_PERMISSIONS,
    RbacPrivilege.COMPANY_USERS_WRITE
  ],
  [RbacPrivilege.MANAGE_OAUTH, RbacPrivilege.COMPANY_SECURITY_WRITE],
  [RbacPrivilege.MANAGE_SAML, RbacPrivilege.COMPANY_SECURITY_WRITE],
  [RbacPrivilege.MANAGE_TEAM_DETAILS, RbacPrivilege.TEAMS_WRITE],
  [RbacPrivilege.MANAGE_TEAM_USER_PERMISSIONS, RbacPrivilege.USERS_WRITE],
  [RbacPrivilege.MANAGE_TEST_SUITES, RbacPrivilege.TEST_SUITES_WRITE],
  [RbacPrivilege.READ_ALL_API_KEYS, RbacPrivilege.ALL_API_KEYS_READ],
  [RbacPrivilege.READ_ALL_CLIENTS, RbacPrivilege.COMPANIES_READ],
  [RbacPrivilege.READ_ALL_INTEGRATION_CLIENTS, RbacPrivilege.CLIENTS_READ],
  [RbacPrivilege.READ_ALL_USERS, RbacPrivilege.COMPANY_USERS_READ],
  [RbacPrivilege.READ_TEAM_ATTACKS, RbacPrivilege.EXPERIMENTS_READ],
  [RbacPrivilege.READ_TEAM_CLIENTS, RbacPrivilege.CLIENTS_READ],
  [RbacPrivilege.READ_TEAM_SCENARIOS, RbacPrivilege.SCENARIOS_READ],
  [RbacPrivilege.READ_TEAM_USERS, RbacPrivilege.USERS_READ],
  [RbacPrivilege.SHARE_ASSET, RbacPrivilege.CLIENTS_READ],
  [RbacPrivilege.SHARE_SCENARIO, RbacPrivilege.SCENARIO_SHARE_WRITE],
  [RbacPrivilege.UNSHARE_SCENARIO, RbacPrivilege.SCENARIO_SHARE_WRITE],

  // The below list of NEW privileges that have multiple mappings to OLD privileges
  // shouldn't be auto-migrated:
  // CLIENTS_READ
  // CLIENTS_WRITE
  // COMPANIES_READ
  // COMPANY_SECURITY_WRITE
  // COMPANY_USERS_WRITE
  // EXPERIMENTS_READ
  // EXPERIMENTS_RUN
  // HALT_ALL
  // HALT_WRITE
  // INTEGRATIONS_READ
  // INTEGRATIONS_WRITE
  // REPORTS_READ
  // SCENARIOS_READ
  // SCENARIOS_WRITE
  // SCENARIO_SHARE_WRITE
  // SCHEDULES_READ
  // SCHEDULES_WRITE
  // TEAMS_WRITE
  // USERS_READ
  // USERS_WRITE

  // Important note: there are multiple OLD_NEW mappings for the below privileges
  // since the new privileges are more fine-grained.
  // EN-6709 manually migrates these but care should be taken in the interim
  // to accurately migrate any similar new ones before the feature is completed.
  [RbacPrivilege.COMPANY_DEFAULT, RbacPrivilege.COMPANIES_WRITE],
  [RbacPrivilege.COMPANY_DEFAULT, RbacPrivilege.COMPANIES_READ],
  [RbacPrivilege.COMPANY_DEFAULT, RbacPrivilege.INTEGRATIONS_READ],
  [RbacPrivilege.COMPANY_DEFAULT, RbacPrivilege.INTEGRATIONS_WRITE],
  [RbacPrivilege.COMPANY_DEFAULT, RbacPrivilege.REPORTS_READ],
  [RbacPrivilege.COMPANY_DEFAULT, RbacPrivilege.TEAMS_READ],
  [RbacPrivilege.COMPANY_DEFAULT, RbacPrivilege.TEST_SUITES_READ],
  [RbacPrivilege.COMPANY_DEFAULT, RbacPrivilege.USERS_READ],
  [RbacPrivilege.COMPANY_DEFAULT, RbacPrivilege.USERS_WRITE],
  [RbacPrivilege.COMPANY_DEFAULT, RbacPrivilege.API_KEYS_READ],
  [RbacPrivilege.COMPANY_DEFAULT, RbacPrivilege.API_KEYS_WRITE],

  [RbacPrivilege.DEACTIVATE_USER, RbacPrivilege.COMPANY_USERS_WRITE],
  [RbacPrivilege.DEACTIVATE_USER, RbacPrivilege.USERS_WRITE],
  [RbacPrivilege.MANAGE_COMPANY_PREFS, RbacPrivilege.COMPANY_PREFERENCES_WRITE],
  [RbacPrivilege.MANAGE_COMPANY_PREFS, RbacPrivilege.COMPANY_SECURITY_WRITE],
  [RbacPrivilege.MANAGE_MFA, RbacPrivilege.COMPANY_SECURITY_WRITE],
  [RbacPrivilege.MANAGE_MFA, RbacPrivilege.USERS_READ],
  [RbacPrivilege.MANAGE_MFA, RbacPrivilege.USERS_WRITE],
  [RbacPrivilege.MANAGE_TEAM_CREDENTIALS, RbacPrivilege.TEAM_SECURITY_READ],
  [RbacPrivilege.MANAGE_TEAM_CREDENTIALS, RbacPrivilege.TEAM_SECURITY_WRITE],
  [RbacPrivilege.MANAGE_WEBHOOKS, RbacPrivilege.WEBHOOKS_READ],
  [RbacPrivilege.MANAGE_WEBHOOKS, RbacPrivilege.WEBHOOKS_WRITE],
  [
    RbacPrivilege.READ_COMPANY_RM_REPORTS,
    RbacPrivilege.RELIABILITY_REPORTS_READ
  ],
  [RbacPrivilege.READ_COMPANY_RM_REPORTS, RbacPrivilege.REPORTS_READ],
  [RbacPrivilege.READ_TEAM_SCHEDULES, RbacPrivilege.EXPERIMENTS_READ],
  [RbacPrivilege.READ_TEAM_SCHEDULES, RbacPrivilege.SCENARIOS_READ],
  [RbacPrivilege.READ_TEAM_SCHEDULES, RbacPrivilege.SCHEDULES_READ],
  [RbacPrivilege.RUN_TEAM_ADHOC_ATTACKS, RbacPrivilege.EXPERIMENTS_RUN],
  [RbacPrivilege.RUN_TEAM_ADHOC_ATTACKS, RbacPrivilege.SCHEDULES_WRITE],
  [RbacPrivilege.RUN_TEAM_ADHOC_ATTACKS, RbacPrivilege.EXPERIMENTS_RUN],
  [RbacPrivilege.RUN_TEAM_SCENARIO_ATTACKS, RbacPrivilege.SCENARIOS_RUN],
  [RbacPrivilege.RUN_TEAM_SCENARIO_ATTACKS, RbacPrivilege.SCENARIOS_WRITE],
  [RbacPrivilege.TEAM_DEFAULT, RbacPrivilege.CLIENTS_READ],
  [RbacPrivilege.TEAM_DEFAULT, RbacPrivilege.CLIENTS_WRITE],
  [RbacPrivilege.TEAM_DEFAULT, RbacPrivilege.EXPERIMENTS_READ],
  [RbacPrivilege.TEAM_DEFAULT, RbacPrivilege.EXPERIMENTS_RUN],
  [RbacPrivilege.TEAM_DEFAULT, RbacPrivilege.EXPERIMENTS_WRITE],
  [RbacPrivilege.TEAM_DEFAULT, RbacPrivilege.HALT_ALL],
  [RbacPrivilege.TEAM_DEFAULT, RbacPrivilege.HALT_WRITE],
  [RbacPrivilege.TEAM_DEFAULT, RbacPrivilege.IMAGES_READ],
  [RbacPrivilege.TEAM_DEFAULT, RbacPrivilege.IMAGES_WRITE],
  [RbacPrivilege.TEAM_DEFAULT, RbacPrivilege.INTEGRATIONS_READ],
  [RbacPrivilege.TEAM_DEFAULT, RbacPrivilege.INTEGRATIONS_WRITE],
  [RbacPrivilege.TEAM_DEFAULT, RbacPrivilege.RELIABILITY_MANAGEMENT_READ],
  [RbacPrivilege.TEAM_DEFAULT, RbacPrivilege.RELIABILITY_MANAGEMENT_RUN],
  [RbacPrivilege.TEAM_DEFAULT, RbacPrivilege.REPORTS_READ],
  [RbacPrivilege.TEAM_DEFAULT, RbacPrivilege.SCENARIOS_READ],
  [RbacPrivilege.TEAM_DEFAULT, RbacPrivilege.SCENARIOS_WRITE],
  [RbacPrivilege.TEAM_DEFAULT, RbacPrivilege.SCHEDULES_READ],
  [RbacPrivilege.TEAM_DEFAULT, RbacPrivilege.SCHEDULES_WRITE],
  [RbacPrivilege.TEAM_DEFAULT, RbacPrivilege.SERVICES_READ],
  [RbacPrivilege.TEAM_DEFAULT, RbacPrivilege.SERVICES_WRITE]
]

const PRIVILEGE_MIGRATION_MAP_OLD_NEW = new Map(PRIVILEGE_MAP)
const PRIVILEGE_MIGRATION_MAP_NEW_OLD = new Map(
  PRIVILEGE_MAP.map(([oldPriv, newPriv]) => [newPriv, oldPriv])
)

/**
 * Order of priority if `shouldAutoMigrate` is true:
 * - if the passed-in privilege is new (post-migration), check that first
 * - if the passed-in privilege is old (pre-migration), check that next
 * - if it's not in either map, check for it in the list directly
 */
export function getHasMigratedPrivilege(
  userPrivileges: RbacPrivilege[],
  privilege: RbacPrivilege,
  shouldAutoMigrate = true
) {
  if (shouldAutoMigrate) {
    const oldPrivilege = PRIVILEGE_MIGRATION_MAP_NEW_OLD.get(privilege)
    if (oldPrivilege) {
      return (
        userPrivileges.includes(privilege) ||
        userPrivileges.includes(oldPrivilege)
      )
    }

    const newPrivilege = PRIVILEGE_MIGRATION_MAP_OLD_NEW.get(privilege)
    if (newPrivilege) {
      return (
        userPrivileges.includes(newPrivilege) ||
        userPrivileges.includes(privilege)
      )
    }
  }

  return userPrivileges.includes(privilege)
}
